import { useMemo } from 'react'
import { useActiveFeatureFlags, useFeatureFlagEnabled, useFeatureFlagVariantKey } from 'posthog-js/react'

import { developmentFlagOverrides, flagsTurnedOn } from './developmentFlagOverrides'
import type { PostHogFeatureFlag } from './posthogFeatureFlags'

export function usePostHogFeatureFlag(key: PostHogFeatureFlag): boolean {
  // `undefined` while flags load, or always when PostHog is not configured - which is when a
  // development build's overrides may answer instead.
  return useFeatureFlagEnabled(key) ?? developmentFlagOverrides()[key] ?? false
}

export function usePostHogFeatureFlagVariant(key: PostHogFeatureFlag): string | boolean | undefined {
  return useFeatureFlagVariantKey(key)
}

/**
 * The flags that are on, for code that reads them as a list - route guards, the navigation.
 * PostHog's list, plus whatever a development build's overrides turn on: with no PostHog the list
 * is empty, which would otherwise hide every flagged route however the overrides are set.
 */
export function useActivePostHogFeatureFlags(): string[] {
  const active = useActiveFeatureFlags()
  const overrides = developmentFlagOverrides()

  return useMemo(() => {
    const fromPostHog = active ?? []
    const turnedOn = flagsTurnedOn(overrides).filter(flag => !fromPostHog.includes(flag))

    return turnedOn.length ? [...fromPostHog, ...turnedOn] : fromPostHog
  }, [active, overrides])
}
